import { GroupedPosition } from '../../../reducer/positions.reducer'
import { Security } from '../../../proto/generated/security'
import { SlTargetType } from './slAndTargetInputs'

export interface CalculatePriceParams {
    inputValue: number
    valueType: SlTargetType
    position: GroupedPosition
    selectedSecurity: Security | undefined
    isStopLoss: boolean // true for SL, false for Target
}

/**
 * Calculate the actual price to send to backend based on input value and type
 * For single securities: converts points/percentage to actual price
 * For baskets: returns MTM value as-is
 * Returns an integer value (rounded) since backend expects values in paise
 */
export function calculatePriceToSend(params: CalculatePriceParams): number {
    const { inputValue, valueType, position, selectedSecurity, isStopLoss } = params

    const isSingleSecurity = () => {
        return (position?.children?.length ?? 0 ) === 1 && position.groupId.startsWith('default_')
    }

    // For baskets, send MTM value directly (rounded to integer)
    if (!isSingleSecurity) {
        return Math.round(inputValue)
    }

    // For single securities in exact mode, inputValue is the exact price
    if (valueType === 'exact') {
        return Math.round(inputValue)
    }

    // For single securities, calculate actual price
    const basePrice = position.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)

    // Determine if position is long or short
    const effectiveQuantity = position.quantity !== 0
        ? position.quantity
        : (() => {
            const children = position.children || []
            const totalPremium = children.reduce((sum, child) => sum + (child.avgPrice * child.quantity), 0)
            return totalPremium > 0 ? Math.abs(totalPremium) : -Math.abs(totalPremium)
        })()

    const isLongPosition = effectiveQuantity > 0

    let calculatedPrice: number

    if (valueType === 'points') {
        // Points mode: entry +/- points
        if (isStopLoss) {
            calculatedPrice = isLongPosition ? basePrice - inputValue : basePrice + inputValue
        } else {
            calculatedPrice = isLongPosition ? basePrice + inputValue : basePrice - inputValue
        }
    } else {
        // Percentage mode: entry +/- percentage
        const percentageChange = (basePrice * inputValue) / 100
        if (isStopLoss) {
            calculatedPrice = isLongPosition ? basePrice - percentageChange : basePrice + percentageChange
        } else {
            calculatedPrice = isLongPosition ? basePrice + percentageChange : basePrice - percentageChange
        }
    }

    // Round to integer since backend expects values in paise
    return Math.round(calculatedPrice)
}

/**
 * Reverse calculate the input value from the stored price
 * Used when loading existing SL/Target orders to show the original input value
 */
export function reverseCalculateInputValue(params: {
    storedPrice: number
    position: GroupedPosition
    selectedSecurity: any
    isStopLoss: boolean
    valueType?: SlTargetType
}): number {
    const { storedPrice, position, selectedSecurity, isStopLoss, valueType } = params

    const isSingleSecurity = (position.children?.length ?? 0) === 1

    // For baskets, the stored value is the MTM value itself
    if (!isSingleSecurity) {
        return storedPrice
    }

    // For single securities in exact mode, return the stored price as-is
    if (valueType === 'exact') {
        return storedPrice
    }

    // For single securities, reverse calculate from the stored price
    const basePrice = position.avgPrice / (selectedSecurity?.priceMultiplier ?? 100)

    // Determine if position is long or short
    const effectiveQuantity = position.quantity !== 0
        ? position.quantity
        : (() => {
            const children = position.children || []
            const totalPremium = children.reduce((sum, child) => sum + (child.avgPrice * child.quantity), 0)
            return totalPremium > 0 ? Math.abs(totalPremium) : -Math.abs(totalPremium)
        })()

    const isLongPosition = effectiveQuantity > 0

    // Reverse calculate: if SL for long position, stored price = basePrice - input
    // So input = basePrice - stored price
    let inputValue: number

    if (isStopLoss) {
        inputValue = isLongPosition ? basePrice - storedPrice : storedPrice - basePrice
    } else {
        inputValue = isLongPosition ? storedPrice - basePrice : basePrice - storedPrice
    }

    return Math.abs(inputValue)
}

